import { deepEqual } from "@core/utils/deepEqual";
import generateUniqueID from "@core/utils/generateUniqueID";
import type { RowSearchResult } from "@ext/serach/utils/SearchRowsModel";
import { emitPluginEvent } from "@plugins/api/events";
import { type MutableRefObject, useCallback, useEffect, useRef } from "react";

const SESSION_ID_LENGTH = 10;

export interface SearchResultMetric {
	url: string;
	title: string;
	catalog: string | undefined;
	type: RowSearchResult["type"];
	position: number;
	isRecommended: boolean;
}

export const buildSearchResultsPayload = (rows: RowSearchResult[]): SearchResultMetric[] =>
	rows.map((row, index) => ({
		url: row.rawResult.url,
		title: row.rawResult.title.map((mark) => mark.text).join(""),
		catalog: row.type === "article" ? row.rawResult.catalog?.title : undefined,
		type: row.type,
		position: index + 1,
		isRecommended: row.type === "article" ? row.rawResult.isRecommended : false,
	}));

export type EmitSearchEvent = typeof emitPluginEvent;

/**
 * The last search sent to analytics; kept outside the dialog so it survives a remount. A remounted dialog builds
 * its params anew, so they are compared by value, not by reference.
 */
export interface ReportedSearch {
	query: string;
	params: unknown;
	searchSessionId: string;
	searchAnalyticsId: number | null;
}

export interface UseSearchAnalyticsArgs {
	open: boolean;
	lastReported?: MutableRefObject<ReportedSearch | null>;
	emit?: EmitSearchEvent;
	userEmail?: string;
}

export interface UseSearchAnalyticsResult {
	/** `params` is everything the search depended on besides the query. */
	onResults: (query: string, rows: RowSearchResult[], catalogName?: string, params?: unknown) => void;
	onLinkClick: (url: string) => void;
}

export const useSearchAnalytics = (args: UseSearchAnalyticsArgs): UseSearchAnalyticsResult => {
	const { open, emit = emitPluginEvent, userEmail } = args;
	const ownLastReported = useRef<ReportedSearch | null>(null);
	const lastReported = args.lastReported ?? ownLastReported;

	const emitRef = useRef(emit);
	emitRef.current = emit;
	const userEmailRef = useRef(userEmail);
	userEmailRef.current = userEmail;

	const sessionIdRef = useRef<string | null>(null);
	const analyticsIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (open) {
			sessionIdRef.current = generateUniqueID(SESSION_ID_LENGTH);
			return;
		}

		sessionIdRef.current = null;
		analyticsIdRef.current = null;
	}, [open]);

	const onResults = useCallback(
		(query: string, rows: RowSearchResult[], catalogName?: string, params?: unknown) => {
			if (!query || !sessionIdRef.current) return;

			// A remounted dialog re-runs the search restored from the shared query: continue that one
			const last = lastReported.current;
			if (last && last.query === query && deepEqual(last.params, params)) {
				sessionIdRef.current = last.searchSessionId;
				analyticsIdRef.current = last.searchAnalyticsId;
				return;
			}

			const searchSessionId = sessionIdRef.current;
			const reported: ReportedSearch = { query, params, searchSessionId, searchAnalyticsId: null };
			lastReported.current = reported;

			void emitRef.current("search:start", {
				query,
				searchSessionId,
				catalogName,
				userEmail: userEmailRef.current,
				onSuccess: (searchAnalyticsId: number) => {
					reported.searchAnalyticsId = searchAnalyticsId;
					analyticsIdRef.current = searchAnalyticsId;
					void emitRef.current("search:results", {
						searchAnalyticsId,
						results: buildSearchResultsPayload(rows),
					});
				},
			});
		},
		[lastReported],
	);

	const onLinkClick = useCallback(
		(url: string) => {
			const searchAnalyticsId = analyticsIdRef.current;
			if (!searchAnalyticsId) return;

			analyticsIdRef.current = null;
			if (lastReported.current?.searchAnalyticsId === searchAnalyticsId)
				lastReported.current.searchAnalyticsId = null;
			void emitRef.current("search:click", { searchAnalyticsId, articleUrl: url });
		},
		[lastReported],
	);

	return { onResults, onLinkClick };
};
